/* eslint-disable max-len */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import { colorBackground, colorText, colorAccent, colorFieldsBorder } from '@/utils/palette';
import {
  FormatBoldIcon,
  FormatItalicIcon,
  FormatUnderlinedIcon,
  StrikethroughSIcon,
  FormatListBulletedIcon,
  FormatListNumberedIcon,
  FormatQuoteIcon,
  LinkIcon,
  ImageIcon,
  TableChartIcon,
  CodeIcon,
  EmojiEmotionsIcon,
  FormatColorFillIcon,
  TextFieldIcon,
} from '@/shared/dynamicImports';
import { useSelector } from 'react-redux';
import { colorDarkText, colorHover, colorIcon } from '@/utils/palette';
import DOMPurify from 'dompurify';

// Styled components for the editor
const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: ${(props) => props.margin || '10px 20px'};
  padding: 10px;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 1px 4px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: 10px;
`;

const ToolbarButton = styled.button`
  margin: 0.1vw;
  padding: clamp(1px, 0.5vw, 3px) clamp(1px, 0.5vw, 3px);
  cursor: pointer;
  border: none;
  background: ${colorBackground};
  color: ${colorIcon};
  font-size: clamp(12px, 0.7vw, 16px);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease-in-out;

  svg {
    transition: fill 0.2s ease-in-out;
    color: ${colorText};
  }

  &:hover {
    background: ${colorHover};
    color: ${colorText};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 4px 2px ${colorAccent};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const StyledToolbarButton = styled(ToolbarButton)`
  color: ${(props) => props.color || 'inherit'};
  &:hover {
    color: ${(props) => props.hoverColor || 'gray'};
  }
`;

const Editor = styled.div`
  border: 1px solid ${colorFieldsBorder};
  padding: 10px;
  min-height: 100px;
  height: ${(props) => props.height || 'auto'};
  outline: none;
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 1px 4px;
  border-radius: 4px;
  color: ${colorText};

  &:empty::before {
    content: attr(data-placeholder);
    color: #888;
    position: absolute;
    top: 10px;
    left: 10px;
    pointer-events: none;
  }

  img {
    max-width: 100%;
    // height: auto;
  }

  blockquote {
    border-left: 4px solid #ccc;
    margin: 1.5em 10px;
    padding: 0.5em 10px;
    color: #fff;
    background: #aaa;
  }
`;

const StyledSelect = styled.select`
  margin: 0 0px;
  padding: 6px 12px;
  font-size: 14px;
  border: transparent;
  border-radius: 4px;
  cursor: pointer;
  background: ${colorBackground};
  color: ${colorText};
  &:hover {
    background: ${colorHover};
    color: ${colorDarkText};
  }
`;

const StyledOption = styled.option`
  padding: 8px;
  font-size: 14px;
  background: #fff;
  color: ${colorText};
  background: ${colorBackground};
`;

const HighlightPalette = styled.div`
  display: flex;
  flex-wrap: wrap;
  position: absolute;d
  background: #fff;
  border: 1px solid #ddd;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  padding: 10px;
  z-index: 100;
`;

const TextColorPalette = styled(HighlightPalette)``;

const EmojiPalette = styled(HighlightPalette)`
  width: 350px;
  top: 40px;
  left: auto;
  right: 0;
`;

const HtmlOutput = styled.div`
  h3 {
    margin-top: 20px;
  }
`;

const RichTextEditorField = ({
  initialContent = '',
  onChange,
  toolbarOptions = {},
  placeholder = '',
  name = '',
  height,
  margin = '5px 20px 0px 0px',
  label,
  error,
  helperText,
  limited = false,
}) => {
  const [htmlContent, setHtmlContent] = useState(initialContent);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const [highlightColor, setHighlightColor] = useState('#ffff00');
  const [showHighlightPalette, setShowHighlightPalette] = useState(false);
  const [showTextColorPalette, setShowTextColorPalette] = useState(false);
  const [selectedFontFamily, setSelectedFontFamily] = useState('');
  const [selectedFontSize, setSelectedFontSize] = useState('');
  const [showEmojiPalette, setShowEmojiPalette] = useState(false);
  const [selectedHeading, setSelectedHeading] = useState('');
  const resizingCellRef = useRef(null);
  const initialMouseXRef = useRef(null);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const darkMode = useSelector((state) => state?.theme?.className);

  const tableStyle = {
    borderCollapse: 'collapse',
    width: '100%',
    border: '1[x solid #ddd',
    color: darkMode === 'dark' ? '#fff' : '#000',
  };
  const fontFamilies = [
    'Arial',
    'Courier New',
    'Georgia',
    'Tahoma',
    'Times New Roman',
    'Verdana',
    'Montserrat',
    'Roboto',
  ];
  const emojis = [
    '😀',
    '😁',
    '😂',
    '🤣',
    '😃',
    '😄',
    '😅',
    '😆',
    '😉',
    '😊',
    '😋',
    '😎',
    '😍',
    '😘',
    '😗',
    '😙',
    '😚',
    '🙂',
    '🤗',
    '🤩',
    '🤔',
    '🤨',
    '😐',
    '😑',
    '😶',
    '🙄',
    '😏',
    '😣',
    '😥',
    '😮',
    '🤐',
    '😯',
    '😪',
    '😫',
    '😴',
    '😌',
    '😛',
    '😜',
    '😝',
    '🤤',
  ];
  const textColors = [
    '#000000', // Black
    '#ff0000', // Red
    '#008000', // Green
    '#0000ff', // Blue
    '#ff00ff', // Magenta
    '#ffa500', // Orange
    '#800080', // Purple
    '#808080', // Gray
  ];
  const highlightColors = [
    '#ffff00', // Yellow
    '#ffcccb', // Red
    '#ccffcc', // Green
    '#ccccff', // Blue
    '#ffeb3b', // Light Yellow
    '#f44336', // Dark Red
    '#8bc34a', // Dark Green
    '#03a9f4', // Light Blue
    '#e91e63', // Pink
    '#9c27b0', // Purple
    '#ff9800', // Orange
    '#795548', // Brown
    '#607d8b', // Grey
  ];

  const handleHeadingChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedHeading(selectedValue);
    execCommand('formatBlock', selectedValue);
  };

  const applyHighlight = (color) => {
    execCommand('backColor', color);
    setShowHighlightPalette(false);
  };

  const toggleHighlightPalette = () => {
    setShowHighlightPalette((prevState) => !prevState);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    updateHtmlContent();
  };

  const removeAllLinks = (content) => {
    // Regular expression to remove all <a> tags and their attributes
    return content.replace(/<a[^>]*>(.*?)<\/a>/g, '$1');
  };

  const updateHtmlContent = () => {
    if (editorRef.current) {
      let content = editorRef.current.innerHTML;
      content = removeAllLinks(content); // Remove all <a> tags
      setHtmlContent(content);
      setIsEditorEmpty(content === '' || content === '<br>' || content === '<div><br></div>');
      if (onChange) {
        onChange(content == '' || content == '<br>' ? null : content);
      }
    }
  };
  const insertLink = () => {
    const url = prompt('Enter the URL');
    if (url) {
      const selection = window.getSelection();
      const selectedText = selection.toString();

      if (selectedText) {
        execCommand('createLink', url);
      } else {
        const linkText = prompt('Enter the text for the link');
        if (linkText) {
          const linkElement = `<a href="${url}" style="text-decoration: underline; color: blue;">${linkText}</a>`;
          execCommand('insertHTML', linkElement);
        }
      }
    }
  };

  const insertCodeBlock = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();

    if (selectedText) {
      const codeElement = `<pre><code>${selectedText}</code></pre>`;
      execCommand('insertHTML', codeElement);
    } else {
      const codeElement = `<div style="border:1px solid #999;border-radius:4px;
        box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 5px;padding:8px;"><pre><code><br></code></pre></div>`;
      execCommand('insertHTML', codeElement);
    }
  };

  // const handlePaste = event => {
  //   event.preventDefault()

  //   const text = (event.clipboardData || window.clipboardData).getData('text')
  //   if (isValidURL(text)) {
  //     execCommand('insertHTML', `<a href="${text}" style="text-decoration: underline; color: blue;">${text}</a>`)
  //   } else {
  //     execCommand('insertText', text)
  //   }
  // }

  const handlePaste = (event) => {
    event.preventDefault();

    // Get HTML content from the clipboard
    let html = (event.clipboardData || window.clipboardData).getData('text/html');
    let text = (event.clipboardData || window.clipboardData).getData('text');

    if (html) {
      // Remove <a> tags and <img> tags but keep other HTML tags
      html = html.replace(/<a[^>]*>(.*?)<\/a>/g, '$1'); // Remove <a> tags but keep inner content
      html = html.replace(/<img[^>]*>/g, ''); // Remove <img> tags entirely

      // Sanitize the HTML content to remove unwanted or potentially harmful elements
      let cleanHtml = DOMPurify.sanitize(html);

      // Insert the sanitized HTML into the editor
      execCommand('insertHTML', cleanHtml);

      // Log for debugging (optional)
      console.log('Sanitized HTML:', cleanHtml);
    } else {
      // If only plain text is available, just insert it as plain text
      execCommand('insertText', text);
    }
  };
  const isValidURL = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = function (e) {
        const imgElement = `<img src="${e.target.result}" alt="Uploaded Image" style="max-width: 100%;" loading="lazy" />`;
        execCommand('insertHTML', imgElement);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please select a valid image file');
    }
  };

  // Attach non-image file
  const handleFileAttachment = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      const fileElement = `<a href="${fileURL}" download="${file.name}" style="text-decoration: underline; color: blue;">${file.name}</a>`;
      execCommand('insertHTML', fileElement);
    }
  };

  const insertTable = () => {
    const rows = parseInt(prompt('Enter number of rows'), 10);
    const cols = parseInt(prompt('Enter number of columns'), 10);

    if (rows > 0 && cols > 0) {
      const tableStyle2 = `width: 100%; border: 1px solid #ccc; border-collapse: collapse; color: ${darkMode === 'dark' ? 'white' : 'black'};`;
      const tableTHStyle = `border: 1px solid #ccc; padding: 8px; font-weight: bold; text-align: center; width: ${100 / cols}%; background-color: ${darkMode === 'dark' ? '#777' : '#ccc'};`;
      let tableHTML = `<table style="${tableStyle2}">`;

      tableHTML += '<tr>';
      for (let c = 0; c < cols; c++) {
        tableHTML += `<th style="${tableTHStyle}">Header ${c + 1}</th>`;
      }
      tableHTML += '</tr>';

      for (let r = 1; r < rows; r++) {
        tableHTML += '<tr>';
        for (let c = 0; c < cols; c++) {
          tableHTML += `<td style="border: 1px solid #ccc; padding: 8px; width: ${100 / cols}%;"></td>`;
        }
        tableHTML += '</tr>';
      }
      tableHTML += '</table>';

      execCommand('insertHTML', tableHTML);
      updateHtmlContent();
    }
  };

  const handleKeyDown = (event) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const currentNode = range.startContainer.parentNode;

    if (event.key === 'Enter' && event.altKey) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const parentNode = range.startContainer.parentNode;

      if (parentNode.nodeName === 'BLOCKQUOTE') {
        event.preventDefault();
        execCommand('insertHTML', '<br>');
      }
    }
    if (event.key === 'Tab') {
      event.preventDefault();

      if (isInsideTable(currentNode)) {
        handleTableNavigation(event.shiftKey);
      } else if (isInsideList(currentNode)) {
        handleListIndentation(event.shiftKey);
      }
    } else if (event.key === 'Backspace') {
      handleBackspace(event);
    } else if (event.key === 'Enter') {
      handleEnter(event);
    }
  };

  const handleEnter = (event) => {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const parentElement = range.startContainer.parentElement;

    if (window.getComputedStyle(parentElement).textDecoration.includes('line-through')) {
      event.preventDefault();
      execCommand('insertHTML', '<br><div></div>');

      const newRange = document.createRange();
      newRange.setStart(parentElement.nextSibling, 0);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  };

  const isInsideTable = (node) => {
    return node.closest('table') !== null;
  };

  const isInsideList = (node) => {
    return node.closest('ul') !== null || node.closest('ol') !== null;
  };

  const handleTableNavigation = (isShift) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    const currentNode = range.startContainer;

    let cell = currentNode.nodeType === 3 ? currentNode.parentElement : currentNode;

    if (cell.tagName !== 'TD' && cell.tagName !== 'TH') return;

    let nextCell;
    if (isShift) {
      nextCell = cell.previousElementSibling || cell.parentElement.previousElementSibling?.lastElementChild;
    } else {
      nextCell = cell.nextElementSibling || cell.parentElement.nextElementSibling?.firstElementChild;
    }

    if (nextCell) {
      const newRange = document.createRange();
      newRange.setStart(nextCell, 0);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  };

  const handleListIndentation = (isShift) => {
    execCommand(isShift ? 'outdent' : 'indent');
  };

  const handleBackspace = (event) => {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);

    if (!range.collapsed) return;

    const startContainer = range.startContainer;
    const startOffset = range.startOffset;

    if (
      startOffset === 0 &&
      startContainer.nodeType === Node.TEXT_NODE &&
      startContainer.parentElement.previousSibling
    ) {
      const currentElement = startContainer.parentElement;
      const previousElement = currentElement.previousSibling;

      event.preventDefault();

      const currentContent = currentElement.innerHTML;
      const newElement = document.createElement(currentElement.tagName);
      newElement.innerHTML = currentContent;

      previousElement.parentNode.insertBefore(newElement, currentElement);
      currentElement.remove();

      const newRange = document.createRange();
      newRange.setStart(newElement, 0);
      selection.removeAllRanges();
      selection.addRange(newRange);

      updateHtmlContent();
    } else if (startContainer.parentElement && startContainer.parentElement.tagName === 'A') {
      const anchorElement = startContainer.parentElement;

      event.preventDefault();

      anchorElement.remove();

      updateHtmlContent();
    }
  };

  const fontSizes = Array.from({ length: 30 }, (_, i) => (i + 8).toString());

  const insertBlockQuote = () => {
    execCommand('formatBlock', 'blockquote');
  };

  const applyTextColor = (color) => {
    execCommand('foreColor', color);
    setShowTextColorPalette(false);
  };

  const toggleTextColorPalette = () => {
    setShowTextColorPalette((prevState) => !prevState);
  };
  const handleFontFamilyChange = (event) => {
    const fontFamily = event.target.value;
    execCommand('fontName', fontFamily);
    setSelectedFontFamily(fontFamily);
  };

  const handleFontSizeChange = (event) => {
    const fontSize = event.target.value;
    execCommand('fontSize', fontSize);
    setSelectedFontSize(fontSize);
  };

  const applyNormalTextStyle = () => {
    document.execCommand('fontSize', false, '2'); // "2" corresponds to approx. 10px in HTML
    execCommand('fontName', 'Arial');
    // execCommand('foreColor', darkMode ? '#fff' : '#000')

    setSelectedFontSize('10');
    setSelectedFontFamily('Arial');
  };

  const toggleEmojiPalette = () => {
    setShowEmojiPalette((prevState) => !prevState);
  };

  const insertEmoji = (emoji) => {
    execCommand('insertText', emoji);
    setShowEmojiPalette(false);
  };
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialContent;
      setIsEditorEmpty(initialContent === '' || initialContent === '<br>' || initialContent === '<div><br></div>');
    }
  }, [initialContent]);
  useEffect(() => {
    const editor = editorRef.current;

    if (editor) {
      editor.addEventListener('keydown', handleKeyDown);
      editor.addEventListener('paste', handlePaste);
      editor.addEventListener('input', updateHtmlContent);
    }

    return () => {
      if (editor) {
        editor.removeEventListener('keydown', handleKeyDown);
        editor.removeEventListener('paste', handlePaste);
        editor.removeEventListener('input', updateHtmlContent);
      }
    };
  }, []);
  const toolbarItems = limited
    ? [
        {
          type: 'button',
          onClick: applyNormalTextStyle,
          tooltip: 'Normal Text',
          icon: <TextFieldIcon />,
        },
        {
          type: 'button',
          onClick: () => execCommand('bold'),
          tooltip: 'Bold Text',
          icon: <FormatBoldIcon />,
        },
        {
          type: 'button',
          onClick: () => execCommand('italic'),
          tooltip: 'Italic Text',
          icon: <FormatItalicIcon />,
        },
        {
          type: 'button',
          onClick: () => execCommand('underline'),
          tooltip: 'Underline the Text',
          icon: <FormatUnderlinedIcon />,
        },
        {
          type: 'button',
          onClick: toggleTextColorPalette,
          tooltip: 'Change Text Color',
          icon: <FormatColorFillIcon />,
        },
      ]
    : [
        {
          type: 'button',
          onClick: applyNormalTextStyle,
          tooltip: 'Normal Text',
          icon: <TextFieldIcon />,
        },
        {
          type: 'button',
          onClick: () => execCommand('bold'),
          tooltip: 'Bold Text',
          icon: <FormatBoldIcon />,
        },
        {
          type: 'button',
          onClick: () => execCommand('italic'),
          tooltip: 'Italic Text',
          icon: <FormatItalicIcon />,
        },
        {
          type: 'button',
          onClick: () => execCommand('underline'),
          tooltip: 'Underline the Text',
          icon: <FormatUnderlinedIcon />,
        },
        {
          type: 'button',
          onClick: () => execCommand('strikeThrough'),
          tooltip: 'Strike Text',
          icon: <StrikethroughSIcon />,
        },
        {
          type: 'select',
          onChange: handleHeadingChange,
          tooltip: 'Heading',
          options: [
            { value: '', label: 'Heading' },
            { value: 'H1', label: 'H1' },
            { value: 'H2', label: 'H2' },
            { value: 'H3', label: 'H3' },
            { value: 'H4', label: 'H4' },
            { value: 'H5', label: 'H5' },
            { value: 'H6', label: 'H6' },
          ],
        },
        {
          type: 'button',
          onClick: () => execCommand('insertOrderedList'),
          tooltip: 'Ordered List',
          icon: <FormatListNumberedIcon />,
        },
        {
          type: 'button',
          onClick: () => execCommand('insertUnorderedList'),
          tooltip: 'Unordered List',
          icon: <FormatListBulletedIcon />,
        },
        {
          type: 'button',
          onClick: insertBlockQuote,
          tooltip: 'Add BlockQuote',
          icon: <FormatQuoteIcon />,
        },
        {
          type: 'button',
          onClick: insertLink,
          tooltip: 'Add Link',
          icon: <LinkIcon />,
        },
        {
          type: 'button',
          onClick: () => fileInputRef.current.click(),
          tooltip: 'Add Image',
          icon: <ImageIcon />,
        },
        {
          type: 'button',
          onClick: insertTable,
          tooltip: 'Add Table',
          icon: <TableChartIcon />,
        },
        {
          type: 'button',
          onClick: insertCodeBlock,
          tooltip: 'Add Code Block',
          icon: <CodeIcon />,
        },
        {
          type: 'button',
          onClick: toggleTextColorPalette,
          tooltip: 'Change Text Color',
          icon: <FormatColorFillIcon />,
        },
        {
          type: 'button',
          onClick: toggleEmojiPalette,
          tooltip: 'Add Emoji',
          icon: <EmojiEmotionsIcon />,
        },
      ];
  return (
    <div>
      {label && <Label style={{ color: `${error ? 'red' : colorText}` }}>{label}</Label>}
      <EditorContainer margin={margin}>
        <Toolbar>
          {toolbarItems.map((item, index) => {
            if (item.type === 'button') {
              return (
                <Tooltip
                  key={index}
                  title={item.tooltip}
                  arrow
                  placement='top'
                >
                  <StyledToolbarButton
                    type='button'
                    onClick={item.onClick}
                    color={colorText}
                    hoverColor={colorAccent}
                  >
                    {item.icon}
                  </StyledToolbarButton>
                </Tooltip>
              );
            } else if (item.type === 'select') {
              return (
                <Tooltip
                  key={index}
                  title={item.tooltip}
                  arrow
                >
                  <StyledSelect
                    onChange={item.onChange}
                    value={selectedHeading}
                    style={{ margin: '0 8px', padding: '5px 10px', cursor: 'pointer' }}
                  >
                    {item.options.map((option) => (
                      <StyledOption
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </StyledOption>
                    ))}
                  </StyledSelect>
                </Tooltip>
              );
            }
            return null;
          })}
        </Toolbar>

        {showHighlightPalette && (
          <HighlightPalette>
            {highlightColors.map((color) => (
              <button
                key={color}
                onClick={() => applyHighlight(color)}
                style={{
                  backgroundColor: color,
                  width: '24px',
                  height: '24px',
                  margin: '4px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              />
            ))}
          </HighlightPalette>
        )}

        {showTextColorPalette && (
          <TextColorPalette>
            {textColors.map((color) => (
              <button
                key={color}
                onClick={() => applyTextColor(color)}
                style={{
                  backgroundColor: color,
                  width: '24px',
                  height: '24px',
                  margin: '4px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              />
            ))}
          </TextColorPalette>
        )}

        {showEmojiPalette && (
          <EmojiPalette>
            {emojis.map((emoji) => (
              <button
                key={emoji}
                onClick={() => insertEmoji(emoji)}
                style={{
                  fontSize: '24px',
                  padding: '4px',
                  margin: '4px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                {emoji}
              </button>
            ))}
          </EmojiPalette>
        )}

        <input
          type='file'
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept='image/*'
          onChange={handleImageUpload}
        />

        <input
          type='file'
          ref={attachmentInputRef}
          style={{ display: 'none' }}
          onChange={handleFileAttachment}
        />

        <Editor
          htmlContent={htmlContent}
          height={height}
          contentEditable
          ref={editorRef}
          suppressContentEditableWarning
          onInput={updateHtmlContent}
          data-placeholder={placeholder}
        ></Editor>
      </EditorContainer>
      {helperText && <HelperText>{helperText}</HelperText>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

RichTextEditorField.propTypes = {
  initialContent: PropTypes.string,
  onChange: PropTypes.func,
  toolbarOptions: PropTypes.object,
  placeholder: PropTypes.string,
};

export default RichTextEditorField;

const HelperText = styled.div`
  font-size: 12px;
  margin-top: 5px;
  color: ${colorText};
`;

const ErrorMessage = styled.div`
  color: red;
  margin-bottom: 20px;
  font-size: 12px;
`;

const Label = styled.div`
  font-size: 12px;
  margin-top: 5px;
`;
